import React from "react";
import { useSelector } from "react-redux";
import "./EsquemaProcedimientoConciliacion.css";
import { fmtDate } from "./laboralExpedienteFormatters";

// id_conciliacion_status: 1 activo · 2 convenio · 3 diferimiento ·
// 4 archivo (incomparecencia solicitante) · 5 archivo (incomparecencia
// patrón) · 6 no conciliación — mismo catálogo que usa DatosExpedienteCard.js
const STATUS = {
  ACTIVO: 1,
  CONVENIO: 2,
  DIFERIMIENTO: 3,
  ARCHIVO_SOLICITANTE: 4,
  ARCHIVO_PATRON: 5,
  NO_CONCILIACION: 6,
};
const FINALES = [STATUS.CONVENIO, STATUS.ARCHIVO_SOLICITANTE, STATUS.ARCHIVO_PATRON, STATUS.NO_CONCILIACION];

// Mismos colores/lógica que el badge de estatus del listado y del encabezado
// del detalle (laboralConciliacionPage.jsx / LaboralConciliacionDetallePage.jsx),
// para que el esquema luzca consistente con el resto de la app.
const STATUS_HEX = {
  green: "#16A34A",
  red: "#DC2626",
  blue: "#1677ff",
  yellow: "#F59E0B",
  gray: "#6B7280",
};
// Fondo sólido pastel a juego con cada color, mismo criterio que el fondo
// sólido #DCE9F5 que usan los nodos "cumplida" por defecto.
const STATUS_BG_HEX = {
  [STATUS_HEX.green]: "#E1F5E7",
  [STATUS_HEX.red]: "#FBE2E1",
  [STATUS_HEX.blue]: "#DCE9F5",
  [STATUS_HEX.yellow]: "#FDF0DA",
  [STATUS_HEX.gray]: "#E7E9EC",
};

const ORDINALES = { 1: "1ra", 2: "2da", 3: "3ra", 4: "4ta", 5: "5ta", 6: "6ta" };
const ordinalAudiencia = (n) => ORDINALES[n] || `${n}a`;

const conclusionInfo = (statusId) => {
  switch (statusId) {
    case STATUS.CONVENIO: return { label: "Convenio", icono: "fa-handshake" };
    case STATUS.ARCHIVO_SOLICITANTE: return { label: "Archivo · Solicitante", icono: "fa-box-archive" };
    case STATUS.ARCHIVO_PATRON: return { label: "Archivo · Patrón", icono: "fa-box-archive" };
    case STATUS.NO_CONCILIACION: return { label: "No conciliación", icono: "fa-ban" };
    default: return { label: "Convenio", icono: "fa-file-circle-check" };
  }
};

// La Historia Procesal (conciliacion_historial) es la fuente más confiable de
// fechas: cada renglón queda fijado al momento real en que se capturó ese
// evento, a diferencia de los campos sueltos del expediente/audiencia que a
// veces se llenan después o quedan en blanco. Se usa como fuente principal y
// solo se cae a los campos del expediente/audiencia si no hay historial.
const historialFechaPorTipo = (historial, tipoEvento) => {
  const fila = (historial || [])
    .filter((h) => (h?.tipo_evento || "").toUpperCase() === tipoEvento)
    .sort((a, b) => new Date(b?.fecha_historial || 0) - new Date(a?.fecha_historial || 0))[0];
  return fila?.fecha_historial || null;
};

const historialFechaPorAudiencia = (historial, idAudiencia) => {
  if (!idAudiencia) return null;
  const fila = (historial || [])
    .filter((h) => h?.tabla_origen === "conciliacion_audiencia" && Number(h?.id_origen) === Number(idAudiencia))
    .sort((a, b) => new Date(b?.fecha_historial || 0) - new Date(a?.fecha_historial || 0))[0];
  return fila?.fecha_historial || null;
};

// Fecha del último pago registrado en el convenio (la que completa los pagos).
// Se toma de pagos_convenio (tabla "Pagos registrados del convenio") y, si no
// hay, del campo convenio[0].fecha_pago que usa la tarjeta de datos.
const fechaUltimoPagoConvenio = (audiencia) => {
  const fechas = (Array.isArray(audiencia?.pagos_convenio) ? audiencia.pagos_convenio : [])
    .map((p) => p?.fecha)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a));
  return fechas[0] || audiencia?.convenio?.[0]?.fecha_pago || null;
};

export default function EsquemaProcedimientoConciliacion({ detalle, audiencias }) {
  const historial = useSelector((state) => state.conciliacion?.historial);

  const statusId = Number(detalle?.id_conciliacion_status ?? 0);
  const enDiferimiento = statusId === STATUS.DIFERIMIENTO;
  const esFinal = FINALES.includes(statusId);
  const items = Array.isArray(audiencias?.items)
    ? audiencias.items
    : Array.isArray(audiencias) ? audiencias : [];
  const numAudiencias = items.length;
  const ultimaAudiencia = items[items.length - 1] || null;
  // No basta con tomar "la última audiencia": se busca la que realmente trae
  // capturado el status final (convenio/no conciliación/archivo), por si el
  // orden de captura no coincidiera con el orden de las audiencias.
  const audienciaConclusion =
    items.slice().reverse().find((a) => Number(a?.id_conciliacion_status) === statusId) || ultimaAudiencia;
  const audienciaConvenio =
    items.find((a) => Number(a?.id_conciliacion_status) === STATUS.CONVENIO) || ultimaAudiencia;
  const isConstancia = Number(audienciaConvenio?.is_constancia_documento ?? 0) === 1;

  // "cumplida" ya no depende solo de que alguien haya capturado la fecha de
  // citatorio/notificación (a menudo quedan en blanco aunque el caso ya
  // avanzó) — si el status ya rebasó esa etapa, se da por hecha.
  const yaHuboAudiencia = statusId > STATUS.ACTIVO;

  const { label: conclusionLabel, icono: conclusionIcono } = conclusionInfo(statusId);

  // Una etapa "N audiencia prejudicial" por cada audiencia que realmente
  // existe, en el mismo orden en que ocurrieron (1ra, 2da, 3ra...). Si el
  // caso sigue activo o en diferimiento se agrega una etapa pendiente más
  // para la siguiente audiencia por venir; si ya concluyó, solo se listan
  // las que de verdad se llevaron a cabo.
  const totalAudienciasAMostrar = enDiferimiento
    ? numAudiencias + 1
    : Math.max(numAudiencias, 1);

  const audienciaNodos = [];
  for (let i = 0; i < totalAudienciasAMostrar; i++) {
    const item = items[i] || null;
    // El dato guardado actual manda; el historial es solo respaldo si la
    // audiencia no tiene su propia fecha (p.ej. quedó desactualizado tras
    // una reprogramación y no se debe mostrar por encima del valor real).
    const fecha = item?.fecha_audiencia || historialFechaPorAudiencia(historial, item?.id);
    // A partir de la 2da audiencia, siempre hubo un diferimiento de por medio
    // (haya pasado ya o esté pendiente), mismo criterio que el pill amarillo
    // "AUDIENCIA #2/#3" del listado (se basa en el número de audiencias, no
    // en si el status actual sigue en diferimiento).
    const esAudienciaDiferida = i >= 1;
    audienciaNodos.push({
      label: `${ordinalAudiencia(i + 1)} Audiencia Prejudicial`,
      icono: "fa-handshake",
      cumplida: !!item || (i === 0 && yaHuboAudiencia) || !!fecha,
      color: esAudienciaDiferida ? STATUS_HEX.yellow : null,
      fecha,
    });
  }

  const fechaEmisionCitatorio = detalle?.fecha_emision_expediente || historialFechaPorTipo(historial, "EXPEDIENTE");
  const fechaRecepcionCitatorio = detalle?.fecha_notificacion || historialFechaPorTipo(historial, "NOTIFICACION");
  const fechaConclusion = audienciaConclusion?.fecha_audiencia || historialFechaPorAudiencia(historial, audienciaConclusion?.id);
  const fechaConstancia =
    audienciaConvenio?.fecha_cumplimiento ||
    audienciaConvenio?.fecha_constancia_documento ||
    audienciaConvenio?.documento_constancia?.created_at;
  const fechaPagoConvenio = fechaUltimoPagoConvenio(audienciaConvenio);

  const NODOS = [
    {
      label: "Emisión Citatorio", icono: "fa-stamp",
      cumplida: !!detalle?.fecha_emision_expediente || statusId > STATUS.ACTIVO || !!fechaEmisionCitatorio,
      fecha: fechaEmisionCitatorio,
    },
    {
      label: "Recepción Citatorio", icono: "fa-paper-plane",
      cumplida: !!detalle?.fecha_notificacion || statusId > STATUS.ACTIVO || !!fechaRecepcionCitatorio,
      // Esta etapa solo existe cuando la notificación la hace el Notificador CCL
      // (tipo_notificado === "actuario"); si el solicitante notifica directo
      // (tipo_notificado === "trabajador") no hay fecha de recepción de citatorio que capturar.
      noAplica: detalle?.tipo_notificado !== "actuario",
      fecha: fechaRecepcionCitatorio,
    },
    ...audienciaNodos,
    {
      label: conclusionLabel, icono: conclusionIcono, cumplida: esFinal,
      // Mientras la audiencia no tenga un dictamen/resultado capturado
      // (statusId sigue en Activo/Diferimiento), no hay nada que concluir
      // todavía — no se debe mostrar "Convenio" como si ya estuviera decidido.
      noAplica: !esFinal,
      color:
        conclusionLabel === "Convenio" ? STATUS_HEX.red :
        conclusionLabel === "No conciliación" ? STATUS_HEX.gray :
        conclusionLabel.startsWith("Archivo") ? STATUS_HEX.gray :
        null,
      fecha: fechaConclusion,
    },
    {
      label: "Pago del Convenio", icono: "fa-money-bill-wave",
      cumplida: !!fechaPagoConvenio,
      color: fechaPagoConvenio ? STATUS_HEX.green : null,
      // Solo existe cuando el caso terminó en convenio; sin pagos capturados
      // se muestra como pendiente con "Sin fecha disponible".
      noAplica: statusId !== STATUS.CONVENIO,
      fecha: fechaPagoConvenio,
    },
    {
      label: "Constancia de Cumplimiento", icono: "fa-circle-check",
      cumplida: (statusId === STATUS.CONVENIO && isConstancia) || !!fechaConstancia,
      color: fechaConstancia || (statusId === STATUS.CONVENIO && isConstancia) ? STATUS_HEX.green : null,
      // Igual que "Convenio": sin dictamen final todavía, o si el dictamen
      // no fue convenio, no hay constancia de cumplimiento que mostrar.
      noAplica: !esFinal || statusId !== STATUS.CONVENIO,
      fecha: fechaConstancia,
    },
  ];

  // Solo se muestra "Cumplimiento de convenio" cuando el caso sí terminó en
  // convenio; las audiencias inexistentes ya se excluyeron arriba.
  const visibles = NODOS.filter((n) => !n.noAplica);

  // La etapa "actual" es la primera sin cumplir de las visibles.
  let actualIdx = -1;
  for (let i = 0; i < visibles.length; i++) {
    if (!visibles[i].cumplida) { actualIdx = i; break; }
  }

  const s = visibles.length;

  return (
    <div className="lce-esq-card">
      <div className="lce-esq-head">
        <div className="lce-esq-title">
          <span className="lce-esq-icon-badge">
            <i className="fa-solid fa-diagram-project" />
          </span>
          Esquema del procedimiento
        </div>
      </div>
      <div className="lce-esq-track">
        <div className="lce-esq-linea" style={{ left: `${50 / s}%`, right: `${50 / s}%` }} />
        <div className="lce-esq-etapas" style={{ gridTemplateColumns: `repeat(${s}, 1fr)` }}>
          {visibles.map((n, idx) => {
            const cls = idx === actualIdx ? "actual" : n.cumplida ? "cumplida" : "";
            return (
              <div className={`lce-esq-etapa ${cls}`} key={idx}>
                <div className="lce-esq-num">{idx + 1}</div>
                <div className="lce-esq-tick" />
                <div className="lce-esq-nodo-wrap">
                  <div
                    className="lce-esq-nodo"
                    style={n.color ? { borderColor: n.color, color: n.color, background: STATUS_BG_HEX[n.color] || "#fff" } : undefined}
                  >
                    <i className={`fa-solid ${n.icono}`} />
                  </div>
                </div>
                <div className="lce-esq-label" style={n.color ? { color: n.color } : undefined}>{n.label}</div>
                <div className="lce-esq-fecha">{n.fecha ? fmtDate(n.fecha) : "Sin fecha disponible"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
